import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import { driver, type Driver, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';

import { router } from '@/router';
import { TOUR_STEPS, SECTION_TUTORIALS } from '@/lib/tourSteps';
import { bubblesSrc, type BubblesMood } from '@/components/tour/MrBubbles';
import { useMarkTourSection } from '@/hooks/useTour';

/**
 * Product tour controller (docs/ONBOARDING.md). Mounted at the app root so
 * both the AppShell "Want a tour?" bar and the outside-shell /welcome page can
 * launch it. Uses the imperative `router` (it lives outside the router tree).
 *
 * Two layers:
 *   - startTour(): the level-1 OVERVIEW — highlights each sidebar section on
 *     /home. Finish → /welcome.
 *   - startTutorial(key): a level-2 deep-dive — navigates to the section's page
 *     and points at the specific buttons that matter (per-step data-tour
 *     anchors, page-main fallback) with detailed narration. On finish it marks
 *     the section complete (mark_tour_section) so the Welcome tile flips to
 *     "Completed", then returns to /welcome. Skip (✕, every step) closes on the
 *     current page and does NOT mark it done.
 *
 * Every popover is branded with Mr. Bubbles (injected header strip) — his
 * expression follows the step's `mood`.
 */

interface TourContextValue {
  startTour: () => void;
  startTutorial: (key: string) => void;
}
const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  startTutorial: () => {},
});

export function useTour(): TourContextValue {
  return useContext(TourContext);
}

function waitForEl(selector: string, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (document.querySelector(selector) || Date.now() - start > timeoutMs) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

interface DriveStep {
  element: string;
  title: string;
  description: string;
  mood: BubblesMood;
}

const MOOD_LABEL: Record<BubblesMood, string> = {
  happy: 'Your Brix guide',
  helpful: 'Here to help',
  excited: "Let's go!",
  thinking: 'Quick tip',
};

/** Prepend the Mr. Bubbles header strip into a driver.js popover. */
function injectBubbles(popover: PopoverDOM, mood: BubblesMood) {
  const wrap = popover.wrapper;
  if (!wrap || wrap.querySelector('.brix-tour-hd')) return;
  const hd = document.createElement('div');
  hd.className = 'brix-tour-hd';

  const img = document.createElement('img');
  img.src = bubblesSrc(mood);
  img.alt = 'Mr. Bubbles';
  img.onerror = () => {
    const span = document.createElement('span');
    span.className = 'brix-tour-fallback';
    span.textContent = '🫧';
    img.replaceWith(span);
  };

  const who = document.createElement('div');
  who.className = 'brix-tour-who';
  who.innerHTML = `<b>Mr. Bubbles</b><span>${MOOD_LABEL[mood]}</span>`;

  hd.appendChild(img);
  hd.appendChild(who);
  wrap.insertBefore(hd, wrap.firstChild);
}

export function TourProvider({ children }: { children: ReactNode }) {
  const driverRef = useRef<Driver | null>(null);
  const completedRef = useRef(false);
  const markSection = useMarkTourSection();

  // Shared runner: navigate to startPath, wait for the first target, then drive
  // the steps. Finish (past the last) → mark section done (if any) + finishPath;
  // skip (✕) → stay put, no marking.
  const run = useCallback(
    (steps: DriveStep[], startPath: string, finishPath: string, sectionKey?: string) => {
      if (steps.length === 0) return;
      driverRef.current?.destroy();
      completedRef.current = false;

      const d = driver({
        showProgress: steps.length > 1,
        allowClose: true,
        stagePadding: 6,
        popoverClass: 'brix-tour',
        nextBtnText: 'Next →',
        prevBtnText: '← Back',
        doneBtnText: 'Finish',
        onPopoverRender: (popover) => {
          const i = d.getActiveIndex() ?? 0;
          injectBubbles(popover, steps[i]?.mood ?? 'helpful');
        },
        steps: steps.map((s) => ({
          element: s.element,
          popover: { title: s.title, description: s.description, side: 'right', align: 'start' },
        })),
        onNextClick: () => {
          const i = d.getActiveIndex() ?? 0;
          if (i >= steps.length - 1) {
            completedRef.current = true;
            d.destroy();
            return;
          }
          d.moveNext();
        },
        onPrevClick: () => d.movePrevious(),
        onCloseClick: () => d.destroy(),
        onDestroyed: () => {
          driverRef.current = null;
          if (completedRef.current) {
            if (sectionKey) markSection.mutate(sectionKey);
            router.navigate(finishPath);
          }
        },
      });
      driverRef.current = d;

      void (async () => {
        if (router.state.location.pathname !== startPath) router.navigate(startPath);
        await waitForEl(steps[0]!.element);
        d.drive();
      })();
    },
    [markSection],
  );

  const startTour = useCallback(() => {
    run(
      TOUR_STEPS.map((s) => ({
        element: s.element,
        title: s.title,
        description: s.description,
        mood: 'happy' as BubblesMood,
      })),
      '/home',
      '/welcome',
    );
  }, [run]);

  const startTutorial = useCallback(
    (key: string) => {
      const t = SECTION_TUTORIALS.find((x) => x.key === key);
      if (!t) return;
      run(
        t.steps.map((s) => ({
          // Point at the specific button when anchored, else the whole page.
          element: s.element ?? '[data-tour="page-main"]',
          title: s.title,
          description: s.description,
          mood: s.mood ?? 'helpful',
        })),
        t.path,
        '/welcome',
        t.key,
      );
    },
    [run],
  );

  return (
    <TourContext.Provider value={{ startTour, startTutorial }}>{children}</TourContext.Provider>
  );
}
